import { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import EmptyState from '../components/ui/EmptyState';
import {
  MANUAL_SECTIONS,
  filterSections,
  groupByCategory,
  isBlockVisible,
  isSectionVisible,
} from '../data/manualContent';

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const ClearIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const renderBlock = (block, lang, idx) => {
  const text = block[lang] || block.ro;
  if (block.type === 'heading') {
    return (
      <h3 key={idx} className="text-lg font-semibold text-slate-900 dark:text-slate-100 mt-4 mb-2">
        {text}
      </h3>
    );
  }
  if (block.type === 'list') {
    const items = block[lang] || block.ro || [];
    return (
      <ul key={idx} className="list-disc pl-6 space-y-1 text-slate-700 dark:text-slate-300 mb-3">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    );
  }
  if (block.type === 'steps') {
    const items = block[lang] || block.ro || [];
    return (
      <ol
        key={idx}
        className="list-decimal pl-6 space-y-1.5 text-slate-700 dark:text-slate-300 mb-3 marker:text-primary-600 marker:font-semibold dark:marker:text-primary-300"
      >
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ol>
    );
  }
  if (block.type === 'tip') {
    return (
      <div
        key={idx}
        className="my-3 rounded-lg border border-primary-200 dark:border-primary-900/60 bg-primary-50/60 dark:bg-primary-950/30 px-4 py-3 text-sm text-primary-900 dark:text-primary-200"
      >
        {text}
      </div>
    );
  }
  return (
    <p key={idx} className="text-slate-700 dark:text-slate-300 leading-relaxed mb-3">
      {text}
    </p>
  );
};

const Manual = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'ro';
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(null);
  const sectionRefs = useRef({});

  const sections = useMemo(
    () => (user ? filterSections(user, query, lang) : []),
    [user, query, lang]
  );

  const grouped = useMemo(() => groupByCategory(sections), [sections]);

  const totalForRole = useMemo(
    () => (user ? MANUAL_SECTIONS.filter((s) => isSectionVisible(s, user)).length : 0),
    [user]
  );

  useEffect(() => {
    if (!activeId && sections.length > 0) {
      setActiveId(sections[0].id);
    }
    if (activeId && !sections.find((s) => s.id === activeId)) {
      setActiveId(sections[0]?.id || null);
    }
  }, [sections, activeId]);

  // Scroll spy: actualizează `activeId` pe măsură ce utilizatorul scrollează prin pagină,
  // ca TOC-ul din stânga să evidențieze automat secțiunea curentă.
  useEffect(() => {
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Alegem intrarea cu cel mai mic top pozitiv (cea mai aproape de viewport top).
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          const id = visible[0].target.dataset.sectionId;
          if (id) setActiveId(id);
        }
      },
      {
        // -100px în top compensează bara fixă + padding;
        // -60% în bottom face ca o secțiune să devină „activă" imediat ce
        // intră ferm în partea superioară a viewportului, nu doar când dispare.
        rootMargin: '-100px 0px -60% 0px',
        threshold: 0,
      }
    );
    sections.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) {
        el.dataset.sectionId = s.id;
        observer.observe(el);
      }
    });
    return () => observer.disconnect();
  }, [sections]);

  const scrollToSection = (id) => {
    setActiveId(id);
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // La deschiderea paginii cu un URL de tipul /manual#<section-id>,
  // sărim automat la secțiunea respectivă (folosit din HelpTooltip).
  useEffect(() => {
    const hash = (location.hash || '').replace('#', '');
    if (!hash || sections.length === 0) return;
    const exists = sections.find((s) => s.id === hash);
    if (!exists) return;
    // lăsăm un tick ca ref-urile să fie setate după render
    const timer = setTimeout(() => scrollToSection(hash), 50);
    return () => clearTimeout(timer);
  }, [location.hash, sections]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader title={t('manual.title')} subtitle={t('manual.subtitle')} />

      {/* Search bar */}
      <Card padding={false} className="p-3">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
            <SearchIcon />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('manual.searchPlaceholder')}
            className="w-full pl-10 pr-10 py-2.5 bg-transparent text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              aria-label={t('manual.clear')}
            >
              <ClearIcon />
            </button>
          )}
        </div>
        {query && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 px-1">
            {t('manual.resultsCount', { count: sections.length, total: totalForRole })}
          </p>
        )}
      </Card>

      {sections.length === 0 ? (
        <EmptyState title={t('manual.emptyTitle')} description={t('manual.emptyDesc')} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* TOC */}
          <aside className="lg:sticky lg:top-20 lg:self-start">
            <Card padding={false} className="p-3">
              <nav className="space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
                {grouped.map((cat) => (
                  <div key={cat.id}>
                    <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      {cat.label[lang] || cat.label.ro}
                    </p>
                    <ul className="space-y-0.5">
                      {cat.sections.map((s) => (
                        <li key={s.id}>
                          <button
                            onClick={() => scrollToSection(s.id)}
                            className={`w-full text-left px-2 py-1.5 rounded-md text-sm transition-colors ${
                              activeId === s.id
                                ? 'bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300 font-medium'
                                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                            }`}
                          >
                            {s.title[lang] || s.title.ro}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>
            </Card>
          </aside>

          {/* Content */}
          <div className="space-y-6 min-w-0">
            {grouped.map((cat) => (
              <section key={cat.id}>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-3">
                  {cat.label[lang] || cat.label.ro}
                </h2>
                <div className="space-y-4">
                  {cat.sections.map((s) => (
                    <Card key={s.id}>
                      <div
                        ref={(el) => {
                          if (el) sectionRefs.current[s.id] = el;
                        }}
                        className="scroll-mt-24"
                      >
                        <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-3">
                          {s.title[lang] || s.title.ro}
                        </h3>
                        <div>
                          {s.content
                            .filter((block) => isBlockVisible(block, user))
                            .map((block, idx) => renderBlock(block, lang, idx))}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Manual;
